import { ChangeDetectionStrategy, Component, computed, inject, signal, OnInit } from '@angular/core';
import { FieldType, FormlyModule } from '@ngx-formly/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { ModulesStore } from 'app/features/admin/modules/modules.store';
import { ModulesService } from 'app/api/services/modules.service';
import { UtilityService } from '../../services/utility.service';
import { ImagePreviewDialogComponent } from '../image-preview.component';
import { FormlyDataCyDirective } from '../../directives/formly-data-cy.directive';
import type { Entry } from '../../../features/admin/modules/modules.model';
import { debugLog } from '../../utils/debug-logger';
import { get } from 'lodash-es';

interface DocumentEntry extends Entry {
  entry: {
    archived: boolean;
    name: string;
    description?: string;
    share?: string;
    file?: {
      documentName?: string;
      documentId?: string;
      blobUrl?: string;
      blobReference?: string;
      createdDateTime?: Date;
    };
  };
}

interface DocumentMetadata {
  documentName?: string;
  documentId?: string;
  blobUrl?: string;
  blobReference?: string;
  createdDateTime?: string | Date;
}

@Component({
  selector: 'formly-field-download-document',
  imports: [
    ReactiveFormsModule,
    FormlyModule,
    MatIconModule,
    MatButtonModule,
    MatTooltip,
    FormlyDataCyDirective,
  ],
  template: `
    <div class="flex flex-col mb-4" [formlyDataCy]="field">
      @if (props.label) {
        <label class="font-medium inline-block mb-2"> {{ props.label }} {{ props.required ? '*' : '' }} </label>
      }

      @if (document()) {
        <div class="fuse-card bg-light-tint my-3 p-4 border">
          <div class="flex items-center justify-between w-full">
            <div class="flex items-center gap-4">
              @if (isImageDocument(document())) {
                <div class="relative cursor-pointer group" (click)="openImagePreview(document())">
                  <img
                    [src]="getDocumentImageUrl(document())"
                    [alt]="document()?.entry?.name || 'Document preview'"
                    class="max-w-24 max-h-16 object-contain rounded shadow-sm transition-transform group-hover:scale-105"
                    (error)="onImageLoadError($event)" />
                  <div
                    class="absolute inset-0  bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 rounded flex items-center justify-center">
                    <mat-icon class=" opacity-0 group-hover:opacity-100 transition-opacity duration-200 icon-size-4">
                      zoom_in
                    </mat-icon>
                  </div>
                </div>
              } @else {
                <mat-icon class="text-hint">{{ getDocumentIcon(document()) }}</mat-icon>
              }

              <div class="flex flex-col">
                <span class="text-md font-medium">{{
                  document()?.entry?.name || getDocumentMetadata(document())?.documentName
                }}</span>
                @if (document()?.entry?.description) {
                  <span class="text-sm text-default">{{ document()?.entry?.description }}</span>
                }
                @if (getDocumentMetadata(document())) {
                  <span class="text-xs text-hint">{{
                    getFileTypeLabel(getDocumentMetadata(document())?.blobReference)
                  }}</span>
                }
              </div>
            </div>

            <div class="flex items-center gap-1">
              @if (isImageDocument(document())) {
                <button
                  mat-icon-button
                  (click)="openImagePreview(document())"
                  type="button"
                  matTooltip="Voorbeeld bekijken">
                  <mat-icon svgIcon="mat_outline:visibility"></mat-icon>
                </button>
              }

              @if (isDocumentPublic(document())) {
                <button mat-icon-button (click)="copyLink(document())" type="button" matTooltip="Link kopiëren">
                  <mat-icon svgIcon="feather:clipboard"></mat-icon>
                </button>
              }

              @if (downloadUrl()) {
                <a
                  mat-icon-button
                  [href]="downloadUrl()"
                  target="_blank"
                  rel="noopener noreferrer"
                  matTooltip="Downloaden">
                  <mat-icon svgIcon="feather:download"></mat-icon>
                </a>
              } @else {
                <button mat-icon-button (click)="downloadDocument(document())" type="button" matTooltip="Downloaden">
                  <mat-icon svgIcon="feather:download"></mat-icon>
                </button>
              }
            </div>
          </div>
        </div>
      } @else {
        <div class="text-sm text-secondary p-4 border rounded bg-light-tint">
          {{ props.emptyText || 'Geen document geselecteerd' }}
        </div>
      }

      @if (props.description) {
        <div class="mt-2 text-sm text-accent">{{ props.description }}</div>
      }
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormlyFieldDownloadDocumentComponent extends FieldType implements OnInit {
  readonly #modulesStore = inject(ModulesStore);
  readonly #modulesService = inject(ModulesService);
  readonly #utilityService = inject(UtilityService);
  readonly #dialog = inject(MatDialog);

  readonly #documentSignal = signal<DocumentEntry | null>(null);
  readonly #downloadUrl = signal<string | null>(null);

  readonly #imageExtensions = new Set(['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg']);

  document = computed(() => this.#documentSignal());
  downloadUrl = computed(() => this.#downloadUrl());

  documentModuleId = computed(() => this.#modulesStore.moduleCodeToId(this.documentModuleCode()));
  documentModuleCode = computed(() => (this.props.documentModuleCode as string) || 'documents');

  ngOnInit() {
    this.initializeDocument();
  }

  /**
   * Initialize the document from (in priority order):
   * 1. documentEntryId  - fetch a specific entry from the documents module by id
   * 2. sourceFieldPath  - a path to another field in the current entry/model
   * 3. The field's own formControl value (default)
   */
  private initializeDocument(): void {
    const documentEntryId = this.props.documentEntryId as string | undefined;
    const sourceFieldPath = this.props.sourceFieldPath as string | undefined;

    if (documentEntryId) {
      this.initializeFromEntryId(documentEntryId);
      return;
    }

    if (sourceFieldPath) {
      this.initializeFromSourcePath(sourceFieldPath);
    } else {
      this.initializeFromFormControl();
    }

    this.prefetchDownloadUrl();
  }

  /**
   * Fetch a documents-module entry by id and use it as the source.
   * Requires the configured documents module to be resolvable via ModulesStore.
   */
  private initializeFromEntryId(entryId: string): void {
    const moduleId = this.documentModuleId();
    if (!moduleId) {
      debugLog('download-document', `No moduleId resolved for code "${this.documentModuleCode()}"`);
      return;
    }

    this.#modulesService.getModuleEntry({ moduleId, entryId }).subscribe({
      next: entry => {
        if (entry) {
          this.#documentSignal.set(entry as unknown as DocumentEntry);
          this.prefetchDownloadUrl();
        }
      },
      error: err => {
        debugLog('download-document', `Failed to fetch entry ${entryId}`, err);
      },
    });
  }

  /**
   * Prefetch the download URL for public documents only.
   * Private documents are resolved on demand: the download button goes through
   * ModulesStore.downloadDocument, which delegates to PrivateDocumentService.
   * That service reads the short-lived SAS URL from the API's JSON response
   * ({ url }) and fetches the file itself with a real Origin — the API no
   * longer 302-redirects to blob storage, because following that cross-origin
   * redirect turned the Origin into `null`, which Azure CORS rejects.
   */
  private prefetchDownloadUrl(): void {
    const doc = this.#documentSignal();
    if (!doc) return;

    const metadata = this.getDocumentMetadata(doc);
    if (!metadata) return;

    // Only prefetch for public documents with blobUrl - use that directly
    if (metadata.blobUrl && this.isDocumentPublic(doc)) {
      this.#downloadUrl.set(metadata.blobUrl);
      return;
    }

    // For private documents, don't prefetch — the download button resolves a
    // short-lived SAS URL via PrivateDocumentService at click time.
  }

  /**
   * Initialize document from a source field path in the entry
   * The path can be:
   * - 'entry.contract' - reads from formState.entry.entry.contract
   * - 'contract' - reads from formState.entry.entry.contract
   */
  private initializeFromSourcePath(sourceFieldPath: string): void {
    // Get document value from entry using the path
    const documentValue = this.getDocumentFromSourcePath(sourceFieldPath);
    if (documentValue && typeof documentValue === 'object') {
      this.#documentSignal.set(documentValue);
    }

    // Also watch model changes for linked fields if model updates
    if (this.model) {
      // Try to get from model as well (for wizard/pages where model is the data source)
      const modelValue = this.getDocumentFromModel(sourceFieldPath);
      if (modelValue && typeof modelValue === 'object') {
        this.#documentSignal.set(modelValue);
      }
    }
  }

  /**
   * Get document value from formState.entry using the source path
   */
  private getDocumentFromSourcePath(sourceFieldPath: string): DocumentEntry | null {
    const entry = this.options?.formState?.entry;
    if (!entry) return null;

    // Normalize the path - remove 'entry.' prefix if present
    let normalizedPath = sourceFieldPath;
    if (normalizedPath.startsWith('entry.')) {
      normalizedPath = normalizedPath.substring(6);
    }

    // Get from entry.entry (the actual entry data)
    const entryData = entry.entry || entry;
    return get(entryData, normalizedPath) || null;
  }

  /**
   * Get document value from the model using the source path
   */
  private getDocumentFromModel(sourceFieldPath: string): DocumentEntry | null {
    if (!this.model) return null;

    // Normalize the path - remove 'entry.' prefix if present
    let normalizedPath = sourceFieldPath;
    if (normalizedPath.startsWith('entry.')) {
      normalizedPath = normalizedPath.substring(6);
    }

    return get(this.model, normalizedPath) || null;
  }

  /**
   * Initialize document from the field's own formControl (default behavior)
   */
  private initializeFromFormControl(): void {
    const currentValue = this.field.formControl.value;
    if (currentValue && typeof currentValue === 'object') {
      this.#documentSignal.set(currentValue);
    }

    // Subscribe to form control changes
    this.field.formControl.valueChanges.subscribe(value => {
      if (value && typeof value === 'object') {
        this.#documentSignal.set(value);
      } else {
        this.#documentSignal.set(null);
      }
    });
  }

  isImageDocument(document: DocumentEntry): boolean {
    if (!document) return false;

    const metadata = this.getDocumentMetadata(document);
    if (!metadata?.blobReference) return false;

    const extension = this.getFileExtension(metadata.blobReference);
    return this.#imageExtensions.has(extension.toLowerCase());
  }

  getDocumentImageUrl(document: DocumentEntry): string | null {
    if (!this.isImageDocument(document)) return null;

    const metadata = this.getDocumentMetadata(document);
    return metadata?.blobUrl || null;
  }

  openImagePreview(document: DocumentEntry): void {
    if (!this.isImageDocument(document)) return;

    const imageUrl = this.getDocumentImageUrl(document);
    if (!imageUrl) return;

    this.#dialog.open(ImagePreviewDialogComponent, {
      data: {
        src: imageUrl,
        title: document.entry?.name || 'Document Preview',
      },
      maxWidth: '95vw',
      maxHeight: '95vh',
      panelClass: 'image-preview-dialog',
    });
  }

  onImageLoadError(event: Event): void {
    const imgElement = event.target as HTMLImageElement;
    imgElement.style.display = 'none';
  }

  getDocumentIcon(document: DocumentEntry): string {
    if (this.isImageDocument(document)) {
      return 'image';
    }

    const metadata = this.getDocumentMetadata(document);
    if (!metadata?.blobReference) return 'description';

    const extension = this.getFileExtension(metadata.blobReference);

    switch (extension.toLowerCase()) {
      case '.pdf':
        return 'picture_as_pdf';
      case '.doc':
      case '.docx':
        return 'description';
      case '.xls':
      case '.xlsx':
        return 'grid_on';
      case '.ppt':
      case '.pptx':
        return 'slideshow';
      case '.zip':
      case '.rar':
        return 'archive';
      case '.mp4':
      case '.avi':
      case '.mov':
        return 'movie';
      case '.mp3':
      case '.wav':
        return 'audio_file';
      default:
        return 'insert_drive_file';
    }
  }

  getFileTypeLabel(fileName: string | null | undefined): string {
    if (!fileName) return '';

    const extension = this.getFileExtension(fileName);

    switch (extension.toLowerCase()) {
      case '.pdf':
        return 'PDF Document';
      case '.doc':
      case '.docx':
        return 'Word Document';
      case '.xls':
      case '.xlsx':
        return 'Excel Spreadsheet';
      case '.ppt':
      case '.pptx':
        return 'PowerPoint';
      case '.jpg':
      case '.jpeg':
        return 'JPEG Afbeelding';
      case '.png':
        return 'PNG Afbeelding';
      case '.gif':
        return 'GIF Afbeelding';
      case '.webp':
        return 'WebP Afbeelding';
      case '.mp4':
        return 'MP4 Video';
      case '.mp3':
        return 'MP3 Audio';
      case '.zip':
        return 'ZIP Archief';
      default:
        return extension ? `${extension.toUpperCase().substring(1)} Bestand` : '';
    }
  }

  private getFileExtension(fileName: string): string {
    const lastDotIndex = fileName.lastIndexOf('.');
    return lastDotIndex !== -1 ? fileName.substring(lastDotIndex) : '';
  }

  downloadDocument(document: DocumentEntry): void {
    const documentMetadata = this.getDocumentMetadata(document);
    if (documentMetadata) {
      this.#modulesStore.downloadDocument({
        moduleId: this.documentModuleId(),
        entryId: document.id,
        documentId: documentMetadata.documentId,
        documentName: documentMetadata.documentName,
        blobUrl: documentMetadata.blobUrl,
      });
    }
  }

  copyLink(document: DocumentEntry): void {
    const documentMetadata = this.getDocumentMetadata(document);
    if (documentMetadata?.blobUrl) {
      navigator.clipboard.writeText(documentMetadata.blobUrl);
      this.#utilityService.notify('Link gekopieerd naar klembord');
    }
  }

  isDocumentPublic(document: DocumentEntry): boolean {
    return document?.entry?.share === 'public' || (document?.publicDocuments && document.publicDocuments.length > 0);
  }

  getDocumentMetadata(document: DocumentEntry): DocumentMetadata | null {
    if (!document) return null;

    // Prefer the candidate that carries a documentId (the private/public arrays can
    // be present but empty/partial; the resolved id lives in entry.file). Falling
    // back to a candidate without documentId previously produced
    // `.../documents/undefined/private` 404s.
    const candidates = [
      document.publicDocuments?.[0],
      document.privateDocuments?.[0],
      document.entry?.file as DocumentMetadata | undefined,
    ];
    return candidates.find(meta => meta?.documentId) ?? candidates.find(Boolean) ?? null;
  }
}
